import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { useIntl } from 'react-intl'

import { fetchExperienceVoucher, fetchBookingDetailsByIds, fetchExperiencesById } from 'actions/ExperienceActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import OrderExperienceDownloadVoucherButton from 'components/Common/OrderView/OrderItemSummaries/OrderExperienceSummaries/OrderExperienceDownloadVoucherButton'
import ExperienceVoucherLayout from 'components/Pages/ExperienceVoucher/ExperienceVoucherLayout'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LineFileDownloadIcon from 'LuxKit/Icons/line/LineFileDownloadIcon'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

const WidgetContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: ${rem(600)};
  border-radius: ${rem(16)};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.flat.small};
  overflow: hidden;
`

const ToggleHeader = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(8)} ${rem(12)};
  border-bottom: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
`

const VoucherViewport = styled.div`
  height: ${rem(520)};
  overflow: auto;
  overscroll-behavior: contain;
`

const VoucherPanelRoot = styled.div`
  height: 100%;
  &[hidden] {
    display: none;
  }
`

const FloatingDownload = styled.div`
  position: absolute;
  inset-inline: 0;
  bottom: ${rem(16)};
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 1;

  > * {
    pointer-events: auto;
    box-shadow: ${props => props.theme.shadow.bottom.medium};
  }
`

const StateContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${rem(24)};
`

interface VoucherEntry {
  orderId: string
  experienceItemIds: Array<string>
  primaryExperienceItemId: string
  experienceOfferId?: string
  groupType?: 'individual_item' | 'shared_provider_booking' | 'uploaded_all_items'
  downloadAllOfferItemsVoucher?: boolean
}

interface Props {
  vouchers: Array<VoucherEntry>
}

interface VoucherPanelProps {
  experienceItemId: string
  active: boolean
}

function VoucherPanel({ experienceItemId, active }: VoucherPanelProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const voucherState = useAppSelector(state => state.experience.experienceVoucher[experienceItemId])
  const bookingDetails = useAppSelector(state => state.experience.bookingDetails[experienceItemId])
  const bookingDetailsError = useAppSelector(state => state.experience.bookingDetailsErrors[experienceItemId])
  const isFetchingBookingDetails = useAppSelector(state => state.experience.fetchingBookingDetails[experienceItemId])
  const experiences = useAppSelector(state => state.experience.experiences)

  useEffect(() => {
    if (!active) return
    if (!voucherState?.data && !voucherState?.fetching && !voucherState?.error) {
      dispatch(fetchExperienceVoucher(experienceItemId))
    }
    if (!bookingDetails && !isFetchingBookingDetails && !bookingDetailsError) {
      dispatch(fetchBookingDetailsByIds([experienceItemId]))
    }
  }, [
    active,
    bookingDetails,
    bookingDetailsError,
    dispatch,
    experienceItemId,
    isFetchingBookingDetails,
    voucherState?.data,
    voucherState?.error,
    voucherState?.fetching,
  ])

  useEffect(() => {
    if (bookingDetails?.experienceOfferId) {
      dispatch(fetchExperiencesById(bookingDetails.experienceOfferId))
    }
  }, [dispatch, bookingDetails?.experienceOfferId])

  const experienceOffer = useMemo(() => {
    return bookingDetails?.experienceOfferId
      ? experiences[bookingDetails.experienceOfferId]
      : undefined
  }, [bookingDetails, experiences])

  const isLoading = !!voucherState?.fetching || !!isFetchingBookingDetails
  const hasError = !!voucherState?.error || !!bookingDetailsError
  const voucher = voucherState?.data

  const errorLabel = intl.formatMessage({
    id: '8jXfW6f_ExEIVttIBX-i',
    defaultMessage: 'Voucher unavailable',
    description: 'Error text shown when an experience voucher cannot be rendered in Trip Planner chat',
  })

  return (
    <VoucherPanelRoot hidden={!active}>
      {isLoading && (
        <StateContainer>
          <LoadingIndicator inline />
        </StateContainer>
      )}
      {!isLoading && hasError && (
        <StateContainer>
          <BodyText variant="medium" colour="neutral-two">
            {errorLabel}
          </BodyText>
        </StateContainer>
      )}
      {!isLoading && !hasError && voucher && (
        <ExperienceVoucherLayout
          voucher={voucher}
          bookingDetails={bookingDetails}
          experienceOffer={experienceOffer}
        />
      )}
    </VoucherPanelRoot>
  )
}

function buildSharedProviderVoucherUrl(voucherUrl: string | undefined, orderId: string, experienceItemId: string): string {
  const baseUrl = voucherUrl || `/account/my-escapes/experiences/${experienceItemId}/voucher`
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}orderId=${encodeURIComponent(orderId)}&isUniqueVoucherCode=true`
}

export default function ExperienceVoucherWidget(props: Props) {
  const { vouchers } = props
  const intl = useIntl()
  const toggleGroupName = useId()

  const [activeIndex, setActiveIndex] = useState(0)

  const safeIndex = Math.min(activeIndex, vouchers.length - 1)
  const activeVoucher = vouchers[safeIndex]
  const showTabs = vouchers.length > 1
  const activeBookingDetails = useAppSelector(state =>
    activeVoucher ? state.experience.bookingDetails[activeVoucher.primaryExperienceItemId] : undefined,
  )

  const handleSegmentChange = useCallback((value: string) => {
    const next = Number(value)
    if (Number.isFinite(next)) setActiveIndex(next)
  }, [])

  const openVoucherPageLabel = intl.formatMessage({
    id: 'wy5y3szOTr6cs5gUfsV1',
    defaultMessage: 'View voucher',
    description: 'Button label to open a shared provider experience voucher from Trip Planner chat',
  })

  const openSharedProviderVoucher = useCallback(() => {
    if (!activeVoucher) return
    window.open(
      buildSharedProviderVoucherUrl(
        activeBookingDetails?.voucherUrl,
        activeVoucher.orderId,
        activeVoucher.primaryExperienceItemId,
      ),
      '_blank',
      'noopener,noreferrer',
    )
  }, [activeBookingDetails?.voucherUrl, activeVoucher])

  if (vouchers.length === 0) return null

  const shouldOpenSharedProviderVoucher =
    activeVoucher?.groupType === 'shared_provider_booking'

  return (
    <WidgetContainer data-testid="experience-voucher-widget">
      {showTabs && (
        <ToggleHeader>
          <SegmentedOptionsGroup
            inputsName={toggleGroupName}
            onValueChange={handleSegmentChange}
          >
            {vouchers.map((voucher, index) => (
              <SegmentedOption
                key={`${voucher.orderId}-${voucher.primaryExperienceItemId}`}
                value={String(index)}
                checked={index === safeIndex}
                title={intl.formatMessage(
                  {
                    id: 'ggfGpapwkYzWP41kLwSk',
                    defaultMessage: 'Voucher {number}',
                    description: 'Toggle label for switching between multiple experience vouchers in the trip planner chat',
                  },
                  { number: index + 1 },
                )}
              />
            ))}
          </SegmentedOptionsGroup>
        </ToggleHeader>
      )}
      <VoucherViewport>
        {vouchers.map((voucher, index) => (
          <VoucherPanel
            key={`${voucher.orderId}-${voucher.primaryExperienceItemId}`}
            experienceItemId={voucher.primaryExperienceItemId}
            active={index === safeIndex}
          />
        ))}
      </VoucherViewport>
      {activeVoucher && shouldOpenSharedProviderVoucher && (
        <FloatingDownload>
          <TextButton
            kind="primary"
            fit="auto"
            size="medium"
            shape="round"
            startIcon={<LineFileDownloadIcon />}
            onClick={openSharedProviderVoucher}
          >
            {openVoucherPageLabel}
          </TextButton>
        </FloatingDownload>
      )}
      {activeVoucher && !shouldOpenSharedProviderVoucher && (
        <FloatingDownload>
          <OrderExperienceDownloadVoucherButton
            key={`${activeVoucher.orderId}-${activeVoucher.primaryExperienceItemId}`}
            orderId={activeVoucher.orderId}
            experienceItemId={activeVoucher.primaryExperienceItemId}
            experienceItemIds={activeVoucher.experienceItemIds}
            experienceOfferId={activeVoucher.experienceOfferId}
            downloadAllOfferItemsVoucher={activeVoucher.downloadAllOfferItemsVoucher}
            size="medium"
            shape="round"
            startIcon={<LineFileDownloadIcon />}
          />
        </FloatingDownload>
      )}
    </WidgetContainer>
  )
}
